import type { Hotspot, Layout, NormRect, Step } from '@mado/schema';
import { productRadiusPx } from '@mado/schema/camera';
import { TYPED_PAUSE_MS } from '@mado/schema/timeline';
import {
  SPOTLIGHT_FEATHER_PX,
  SPOTLIGHT_PAD_PX,
  spotlightRect,
  spotlightWanted,
} from '@mado/schema/camera';
import { backButton, boldIn, cardButton, cardFoot, controlSpan, tile } from './callout';
import { overlap } from './cue-label';
import { el } from './stage';
import type { Strings } from './strings';
import { EASE, dur, linear, reflow, tween } from './motion';

/** Minimum comfortable touch target (DESIGN-SYSTEM §6 rule 11). */
const MIN_TARGET = 44;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Normalized asset rect → viewport CSS pixels, using the stage's transform. */
export function projectRect(geom: NormRect, layout: Layout, asset: { w: number; h: number }): Rect {
  return {
    x: layout.tx + geom.x * asset.w * layout.scale,
    y: layout.ty + geom.y * asset.h * layout.scale,
    w: geom.w * asset.w * layout.scale,
    h: geom.h * asset.h * layout.scale,
  };
}

/** The moves the overlay mirrors while a step arrives — see `Overlay.follow`. */
export interface Follow {
  /** The incoming layer's entrance: the same transform strings, origin and transition. */
  layer?: { from: string; to: string; origin: string; transition: string } | null;
  /** The camera's move from `from` to `to` (the layout the overlay is drawn at). */
  cam?: { from: Layout; to: Layout; transition: string } | null;
}

/**
 * The transform, applied to something drawn at layout `to`, that puts it
 * where layout `at` shows the same asset point: p → at.t + (p − to.t)·at.s/to.s.
 *
 * Written as a list whose first two functions are the camera's own
 * (`translate`, `scale`) and whose tail is constant, so the browser
 * interpolates it function by function exactly as it interpolates the stage's
 * `translate3d(tx,ty,0) scale(s)`: at every instant of the move the overlay
 * maps onto the picture. At `at === to` it is the identity.
 */
export function followTransform(at: Layout, to: Layout): string {
  return (
    `translate(${at.tx}px,${at.ty}px) scale(${at.scale}) ` +
    `scale(${1 / to.scale}) translate(${-to.tx}px,${-to.ty}px)`
  );
}

/** Grow a rect about its centre to at least MIN_TARGET on both axes. */
function ensureTarget(r: Rect): Rect {
  const w = Math.max(r.w, MIN_TARGET);
  const h = Math.max(r.h, MIN_TARGET);
  return { x: r.x - (w - r.w) / 2, y: r.y - (h - r.h) / 2, w, h };
}

/**
 * `r` cut to `bounds` (the phone story's window) — or `r` unchanged when
 * there are no bounds, or when it lies wholly outside them (a target a pan
 * has carried off the window keeps its own rect: it is clipped from view
 * either way, and a zero-size box would be worse than none).
 */
export function clipRect(r: Rect, bounds: Rect | null): Rect {
  if (!bounds) return r;
  if (
    r.x >= bounds.x &&
    r.y >= bounds.y &&
    r.x + r.w <= bounds.x + bounds.w &&
    r.y + r.h <= bounds.y + bounds.h
  )
    return r;
  const x0 = Math.max(r.x, bounds.x);
  const y0 = Math.max(r.y, bounds.y);
  const x1 = Math.min(r.x + r.w, bounds.x + bounds.w);
  const y1 = Math.min(r.y + r.h, bounds.y + bounds.h);
  if (x1 <= x0 || y1 <= y0) return r;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** How far the pulse ring reaches past its target: its 6 px glow, plus a little air. */
export const PULSE_REACH_PX = 8;
/** The pulse grows by this share of its size at its peak (styles.ts `mado-pulse`, scale 1.06). */
const PULSE_GROW = 0.03;

/**
 * The pulse ring's inset from its target's box, per side (top, right, bottom, left): 0 inside the
 * window, more at a side that sits on the window's edge, so the whole ring is drawn inside it. A
 * control flush with the screen's edge is flush with the window's when the camera frames that
 * edge, and its ring was cut there (Talentika s3, Add block, player-feel 5, 2026-10-15). The
 * camera cannot show past the screen; the ring comes in instead. The button keeps its box.
 */
export function pulseInset(r: Rect, area: Rect): [number, number, number, number] {
  const side = (dist: number, len: number): number =>
    Math.max(0, Math.min(len / 3, PULSE_REACH_PX + len * PULSE_GROW - dist));
  return [
    side(r.y - area.y, r.h),
    side(area.x + area.w - (r.x + r.w), r.w),
    side(area.y + area.h - (r.y + r.h), r.h),
    side(r.x - area.x, r.w),
  ];
}

function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Slide `r` (no larger than `bounds` on an axis, or it is pinned to the start) inside `bounds`. */
function shiftInto(r: Rect, bounds: Rect | null): Rect {
  if (!bounds) return r;
  const x = clamp(r.x, bounds.x, Math.max(bounds.x, bounds.x + bounds.w - r.w));
  const y = clamp(r.y, bounds.y, Math.max(bounds.y, bounds.y + bounds.h - r.h));
  return { x, y, w: Math.min(r.w, bounds.w), h: Math.min(r.h, bounds.h) };
}

export interface OverlayCallbacks {
  /** `pointer`: pressed by a mouse or a finger (a keyboard's or a script's click is not). */
  onHotspot(h: Hotspot, pointer?: boolean): void;
  /** After each render: whether a target sits under the chrome bar. */
  onChromeOverlap?(overlap: boolean): void;
  /** The chrome bar's height as laid out; the band a target must clear (`overlapsChrome`). */
  chromeHeight?(): number;
  /**
   * The step's words are being said aloud (Watch, sound on, a voiced clip), so
   * its bubble would only repeat them. Absent: never.
   */
  spokenAloud?(step: Step): boolean;
  /**
   * The step's narration as the viewer reads it (personalised), or ''. Shown
   * under the tooltip's action line, and on the guide card of a step with
   * nothing to tap, whenever the words are not being said aloud.
   */
  say?(step: Step): string;
  /**
   * Go on from a step with nothing to tap: the guide card's Next. The same
   * path as the bar's Next arrow (one step event, nothing else). Absent: no
   * guide card, the step keeps its plain tooltip as before.
   */
  /** `pointer`: a mouse or finger pressed it (a keyboard's Enter or Space is detail 0). */
  onNext?(pointer: boolean): void;
  /** "3 / 9": shown in the instruction while the bar has stepped aside for the target. */
  progress?(): string;
  /**
   * Click-through: the instruction is the last card of the step's callouts, in their box and
   * foot (founder, 2026-10-15: "these two designs are a bit too far apart"). `count`: "3 / 3"
   * after two callouts, '' when it is the step's only card; `back`: Back goes somewhere. Null or
   * absent (Watch): the plain tooltip.
   */
  foot?(step: Step): { count: string; back: boolean } | null;
  /** Watch is playing: it advances by itself, so a guide card has no buttons. */
  watching?(): boolean;
  /** A card's Back: the card before, or the step before. `pointer` as `onNext`. */
  onBack?(pointer: boolean): void;
  /**
   * The bar sits under the device frame, not over the screen (runtime.ts `dock`): nothing on the
   * screen is under it, so nothing keeps clear of it.
   */
  docked?(): boolean;
  /**
   * A place for the step's instruction outside the screen, under the docked bar, when there is
   * room for it there (a tablet on a phone); null: on the screen, beside its target.
   */
  dockCard?(): HTMLElement | null;
}

/**
 * Does the chrome bar have to step aside for these targets? True when a
 * visible target's vertical centre lies in the bar's band at the bottom of
 * the viewport — the tab-bar item, the full-width button — so that tapping
 * the target's centre reaches the target and nothing dims it. A target that
 * merely grazes the band (a list row above a tab bar) or spans the whole
 * screen (a scroll area, a swipe surface) keeps the bar: its centre is
 * clear, and a control bar over a scroll area is what a viewer expects.
 * Anything below the viewport is not on screen and does not count.
 */
export function overlapsChrome(rects: Rect[], hostH: number, band = CHROME_CLEAR_PX): boolean {
  if (!(hostH > 0)) return false;
  const top = hostH - band;
  return rects.some((r) => r.y < hostH && r.y + r.h / 2 >= top);
}

export interface OverlayStyle {
  /** `theme.motion === 'cinematic'`: the soft spotlight replaces the hard scrim. */
  cinematic: boolean;
  /** The spotlight's dim, as a CSS colour — derived from the theme background. */
  spotColor: string;
  /** The demo's words (absent: English). */
  s?: Strings;
}

/** What the overlay waits for before the spotlight and tooltip appear. */
export interface RenderTiming {
  /** ms until the camera has settled on this step; 0 when it is already there. */
  settleMs: number;
  /**
   * Leave the layer-entrance follow (`Overlay.follow`'s `layer`) where it is:
   * a redraw while the step's picture is still sliding or growing in must
   * keep riding along with it, not snap to where it comes to rest.
   */
  keepEntrance?: boolean;
  /**
   * Shown as already in: no fade for the tooltip and the spotlight. A silent walk redraws
   * the targets at each camera move (runtime `trackOverlay`); a fade each time would blink.
   */
  instant?: boolean;
  /**
   * Draw the targets but do not start a step's own typing yet: a silent walk
   * redraws at each camera move, and typing restarted at every move would
   * stutter. The walk's last redraw types.
   */
  noTyping?: boolean;
}

/**
 * Everything drawn above the pixels: tap targets, coach marks, simulated typing.
 *
 * Hotspots are real <button>s, not divs — that is what gives keyboard traversal,
 * screen-reader names, and focus rings for free rather than reimplemented badly.
 */
export class Overlay {
  readonly root: HTMLElement;
  private typing: { cancel(): void } | null = null;
  /** A click on a type target is typing out; its end advances the step. */
  private typingOnClick = false;
  private nudgeTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The guide card of a step with nothing to tap (`renderGuide`). Lives on
   * the root, not in the follow wrappers: it is the player's furniture, not
   * part of the picture, and does not ride the camera.
   */
  private guide: HTMLElement | null = null;
  /** The step has a guide card's place, drawn or not (Watch with nothing to say draws none). */
  private guideWanted = false;
  /** The place outside the screen the step's card went to (`dockCard`), to empty with the rest. */
  private dockBox: HTMLElement | null = null;
  /** The product's corner radius read off its site (`theme.radius`): the pulse takes its corners. */
  radius: number | undefined = undefined;
  /** The step's instruction is the headline above the window (cards.ts `planCards`): no bubble. */
  headline = false;
  /**
   * Click-through callouts are open (runtime `openCallouts`): the step's instruction waits for
   * the last one, one thing at a time. The targets are drawn and live meanwhile.
   */
  callouts = false;
  /**
   * Phone walkthrough: the picture is already close on the target, the
   * headline above it saying what to do — the spotlight's dim over the rest
   * only greys the product out. Off unless a step turns it on explicitly.
   */
  walk = false;
  /**
   * Phone story: the framed window, in viewport px. Every target and the
   * typed box are cut to it, and the layer is clipped to it besides (the
   * pulse ring, a focus outline, the spotlight's dim): nothing drawn for the
   * picture ever spills past the picture's frame into the margin. Null: the
   * whole viewport is the picture.
   */
  private bounds: Rect | null = null;

  setBounds(b: Rect | null, radius = 0): void {
    this.bounds = b;
    const vw = this.root.clientWidth;
    const vh = this.root.clientHeight;
    this.root.style.clipPath =
      b && vw > 0 && vh > 0
        ? `inset(${b.y}px ${vw - b.x - b.w}px ${vh - b.y - b.h}px ${b.x}px round ${radius}px)`
        : '';
  }

  constructor(
    private cb: OverlayCallbacks,
    private readonly style: OverlayStyle = { cinematic: false, spotColor: 'rgba(20,18,16,.38)' },
  ) {
    this.root = el('div', 'mado-overlay');
    this.lay = el('div', 'mado-follow');
    this.cam = el('div', 'mado-follow');
    this.lay.appendChild(this.cam);
    this.root.appendChild(this.lay);
  }

  /**
   * Two wrappers every overlay element lives in. `lay` mirrors the incoming
   * layer's entrance (a slide, the reveal's growth), `cam` the camera's move
   * inside it (overlays.ts `follow`). At rest both are untransformed.
   */
  private readonly lay: HTMLElement;
  private readonly cam: HTMLElement;

  clear(keepEntrance = false): void {
    this.typing?.cancel();
    this.typing = null;
    this.typingOnClick = false;
    if (this.nudgeTimer) clearTimeout(this.nudgeTimer);
    this.nudgeTimer = null;

    this.cam.replaceChildren();
    this.clearDock();
    this.guide?.remove();
    this.guide = null;
    this.guideWanted = false;
    for (const w of keepEntrance ? [this.cam] : [this.lay, this.cam]) {
      w.style.transition = 'none';
      w.style.transform = '';
      w.style.transformOrigin = '';
    }
  }

  /**
   * Keep the targets on the picture while it moves.
   *
   * The overlay is drawn at the step's target layout. While the incoming layer
   * is still sliding or growing in, and while the camera is still settling,
   * the picture is somewhere else — and a tap on the button the viewer can
   * see would land beside its target. So the overlay runs the same moves: the
   * layer's entrance on `lay` (same transform strings, origin, transition),
   * and on `cam` the transform that maps the target layout onto the camera's
   * layout at each instant (`followTransform`), on the same clock and curve
   * as the stage. The browser hit-tests transformed elements where they are
   * drawn, mid-transition included, so a tap lands on what is on screen.
   *
   * Sets the starting pose now (and commits it); the returned function starts
   * the moves, and must be called in the same task as the picture's own moves
   * start, with no style flush in between: the transitions then begin on the
   * same frame. WebKit starts a transition created by a forced flush at once
   * and one created by the next frame's flush a frame later — a reflow
   * between the picture's moves and these put the targets a frame (tens of
   * pixels, mid-reveal) behind the picture.
   */
  follow(f: Follow): () => void {
    const { lay, cam } = this;
    if (f.layer) {
      lay.style.transformOrigin = f.layer.origin;
      lay.style.transform = f.layer.from;
    }
    if (f.cam) cam.style.transform = followTransform(f.cam.from, f.cam.to);
    if (f.layer || f.cam) reflow(this.root);
    return () => {
      if (!f.layer && !f.cam) return;
      if (f.layer) {
        lay.style.transition = f.layer.transition;
        lay.style.transform = f.layer.to;
      }
      if (f.cam) {
        cam.style.transition = f.cam.transition;
        cam.style.transform = followTransform(f.cam.to, f.cam.to);
      }
    };
  }

  /**
   * After a miss: one firmer beat of every target's pulse ring, so the eye is
   * drawn to where the tap should have gone. Transform and opacity only, and
   * a class that is taken off again — the ordinary pulse resumes by itself.
   */
  nudge(): void {
    // Callouts open: only the card's Next beats, the way on from here. The target's ring stays
    // aside with its pulse (founder, 2026-10-15: a press anywhere drew a big ring round Jobs).
    const targets = this.root.querySelectorAll<HTMLElement>(
      this.callouts ? '.mado-callout-next' : '.mado-hotspot',
    );
    if (!targets.length) return;
    if (this.nudgeTimer) clearTimeout(this.nudgeTimer);
    // The instruction gets the same beat: its words say what the tap should
    // have been, the ring (shown even while a narration ring is lit,
    // styles.ts) where.
    const tip = this.callouts ? null : this.root.querySelector<HTMLElement>('.mado-tooltip');
    const all = tip ? [...targets, tip] : [...targets];
    const cls = (n: HTMLElement) =>
      n.classList.contains('mado-hotspot') ? 'mado-hotspot--nudge' : 'mado-tooltip--nudge';
    all.forEach((t) => {
      t.classList.remove(cls(t));
      reflow(t);
      t.classList.add(cls(t));
    });
    this.nudgeTimer = setTimeout(
      () => {
        this.nudgeTimer = null;
        all.forEach((t) => t.classList.remove(cls(t)));
      },
      dur(NUDGE_MS) + 40,
    );
  }

  /** Render this step's interactive layer for the current geometry. */
  render(
    step: Step,
    layout: Layout,
    asset: { w: number; h: number },
    timing: RenderTiming = { settleMs: 0 },
  ): void {
    // A redraw of the step being typed into after a click (the phone camera arriving on the
    // control, a cue frame) would cancel the typing and the advance it ends in: the viewer saw the
    // text typed and the step stuck (e2e analytics, iPhone and Pixel, 2026-10-22). Keep the frame,
    // as `reposition` does; the step is about to change. Another step's render still clears it.
    if (this.typingOnClick && step.id === this.typingStepId) return;
    this.clear(timing.keepEntrance);

    // Everything here is placed where the picture comes to rest; while the
    // picture is still travelling `follow` carries it along, so the targets
    // are live where the controls are seen (a fast viewer's tap lands) but
    // unseen: a pulse ring or a typed box on a control still in motion reads
    // as a bug, not as speed.
    const settle = dur(timing.settleMs);
    // Snap, never fade, to hidden: a transition left over from the last
    // render would otherwise carry the old overlay's fade-out into this one.
    this.root.style.transition = 'none';
    this.root.style.opacity = settle > 0 ? '0' : '1';

    // `Step.effects?.spotlight === false`: a per-step override, off. Absent
    // or true is today's automatic behaviour, unchanged — except on a phone's
    // walk and a desktop's guided overview step, where it is off unless asked.
    const spotlightOn = spotlightWanted(step, {
      walk: this.walk,
      motion: this.style.cinematic ? 'cinematic' : 'static',
    });
    const pulseOn = step.effects?.pulse !== false;

    // Cinematic: a soft spotlight on the targets, lit once the camera rests.
    // Static: the hard scrim of old, on a lone tooltip target, unchanged. A
    // hotspot with no text (CLAUDE.md "several hotspots per step") is not a
    // tooltip target: `.text.trim()`, not just a non-null `annotation`.
    const spotlight =
      spotlightOn &&
      !this.style.cinematic &&
      !!step.annotation?.text.trim() &&
      step.hotspots.length === 1;
    if (this.style.cinematic && spotlightOn) {
      this.renderSpot(step, layout, asset, timing.settleMs, timing.instant);
    }

    const rects: Rect[] = [];
    for (const h of step.hotspots) {
      // In a story, cut to the window first, then grown to a touch target
      // and slid back inside it: the ring is drawn where the viewer sees
      // the control, never past the frame.
      const seen = projectRect(h.geom, layout, asset);
      const cut = clipRect(seen, this.bounds);
      const r =
        this.bounds && overlaps(seen, this.bounds)
          ? shiftInto(ensureTarget(cut), this.bounds)
          : ensureTarget(seen);
      rects.push(r);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `mado-hotspot${h.shape === 'ellipse' ? ' mado-hotspot--ellipse' : ''}`;
      btn.style.left = `${r.x}px`;
      btn.style.top = `${r.y}px`;
      btn.style.width = `${r.w}px`;
      btn.style.height = `${r.h}px`;
      if (h.shape !== 'ellipse') {
        btn.style.borderRadius = `${productRadiusPx({ radius: this.radius }, asset) * layout.scale + 2}px`;
      }
      btn.setAttribute(
        'aria-label',
        h.label || step.name || (this.style.s?.gateContinue ?? 'Continue'),
      );
      // Light enough to guide without draining the product being demoed. The
      // pulse ring already says where to tap; this only has to bias the eye, and
      // on a dark app a heavy scrim turns white headlines grey.
      if (spotlight) btn.style.boxShadow = '0 0 0 9999px rgba(14,14,16,.26)';

      if (pulseOn) {
        const pulse = el('span', 'mado-pulse');
        const area = this.bounds ?? {
          x: 0,
          y: 0,
          w: this.root.clientWidth,
          h: this.root.clientHeight,
        };
        // The pulse hugs the control as it is seen, not the tap area grown round a small one
        // (a 44 px frame round a toolbar icon read as a frame round its neighbours, 2026-10-21).
        const edge = [
          cut.y - r.y,
          r.x + r.w - cut.x - cut.w,
          r.y + r.h - cut.y - cut.h,
          cut.x - r.x,
        ];
        if (area.w > 0 && area.h > 0) {
          pulseInset(r, area).forEach((v, i) => (edge[i] = Math.max(edge[i]!, v)));
        }
        const [t, ri, b, l] = edge.map((v) => Math.max(0, v));
        if (t || ri || b || l) pulse.style.inset = `${t}px ${ri}px ${b}px ${l}px`;
        btn.appendChild(pulse);
      }
      const hint = gestureHint(h);
      if (hint) {
        btn.classList.add(`mado-hotspot--${h.gesture}`);
        btn.appendChild(hint);
      }

      // A "type" target with text types it out on click, then moves on: the
      // viewer does the typing, the way they would in the product.
      const typesOnClick = h.gesture === 'type' && step.input?.target === h.id;
      btn.addEventListener('click', (ev) => {
        ev.preventDefault();
        // The second click of a double click lands on the next step's control,
        // drawn in the same place: it is not a press of it.
        if (ev.detail > 1) return;
        if (!typesOnClick) return this.cb.onHotspot(h, ev.detail > 0);
        this.beginTyping(step, layout, asset, () => this.cb.onHotspot(h));
      });
      this.cam.appendChild(btn);
    }
    // Decided where the targets rest (`layout` is the step's target framing,
    // set before the camera arrives), so the bar is already out of the way
    // when a fast viewer taps mid-move.
    const band = chromeBand(this.cb.chromeHeight?.() ?? 0);
    const underBar = !this.cb.docked?.() && overlapsChrome(rects, this.root.clientHeight, band);
    this.cb.onChromeOverlap?.(underBar);

    const quiet = !this.callouts && !this.cb.spokenAloud?.(step);
    if (quiet && !step.hotspots.length && this.cb.onNext) {
      // Nothing to tap: the step's words and a Next button, so the way on is seen.
      this.renderGuide(step, layout, asset, timing.settleMs, timing.instant);
    } else if (step.annotation?.text.trim() && quiet && !this.headline) {
      // No text, no bubble: a hotspot without a tooltip (CLAUDE.md "several
      // hotspots per step") gets its pulse ring and nothing else.
      this.renderTooltip(step, layout, asset, timing.settleMs, timing.instant, underBar);
    }
    // Text on a plain target (older documents, pipeline-seeded input) still
    // plays by itself as the step opens — once the camera has arrived on it.
    const typedInto = step.input && step.hotspots.find((x) => x.id === step.input!.target);
    if (typedInto && typedInto.gesture !== 'type' && !timing.noTyping) {
      this.renderTyping(step, layout, asset, undefined, settle);
    }

    if (settle > 0) {
      reflow(this.root);
      this.root.style.transition = `opacity 220ms ${EASE} ${settle}ms`;
      this.root.style.opacity = '1';
    }
  }

  /**
   * Type the step's text into its target, then `done` — the click on a type
   * target, also driven by watch mode. Returns false when the step has no
   * text to type or is already typing (the advance is on its way).
   */
  beginTyping(
    step: Step,
    layout: Layout,
    asset: { w: number; h: number },
    done: () => void,
  ): boolean {
    if (this.typing) return false;
    const input = step.input;
    if (!input || !step.hotspots.some((h) => h.id === input.target)) return false;
    this.root.querySelector('.mado-hint--type')?.remove();
    this.typingOnClick = true;
    this.typingStepId = step.id;
    this.renderTyping(step, layout, asset, () => {
      this.typingOnClick = false;
      done();
    });
    return true;
  }

  /**
   * Stop typing where it is (watch mode interrupted mid-word). The typed box
   * is removed rather than frozen: a viewer taking over then taps the field
   * and types it themselves, from the start, like a first attempt.
   */
  cancelTyping(): boolean {
    if (!this.typing) return false;
    this.typing.cancel();
    this.typing = null;
    this.typingOnClick = false;
    this.root.querySelector('.mado-typed')?.remove();
    return true;
  }

  private anchorRect(step: Step, layout: Layout, asset: { w: number; h: number }): Rect | null {
    const a = step.annotation!;
    if (typeof a.anchor === 'string') {
      const h = step.hotspots.find((x) => x.id === a.anchor);
      return h ? projectRect(h.geom, layout, asset) : null;
    }
    return projectRect(a.anchor, layout, asset);
  }

  /**
   * Dim everything but the step's targets. A radial gradient set once per
   * step — clear over the targets, feathered out to the dim — on a layer whose
   * only moving property is opacity, delayed until the camera has settled so
   * the eye follows the move first and finds the target lit on arrival.
   */
  private renderSpot(
    step: Step,
    layout: Layout,
    asset: { w: number; h: number },
    settleMs: number,
    instant = false,
  ): void {
    const geom = spotlightRect(step);
    if (!geom) return;
    const r = projectRect(geom, layout, asset);
    // A target that a pan has carried off the frame lights nothing.
    const w = this.root.clientWidth;
    const h = this.root.clientHeight;
    if (r.x + r.w < 0 || r.y + r.h < 0 || (w > 0 && r.x > w) || (h > 0 && r.y > h)) return;
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    const clearX = r.w / 2 + SPOTLIGHT_PAD_PX;
    const clearY = r.h / 2 + SPOTLIGHT_PAD_PX;
    const rx = clearX + SPOTLIGHT_FEATHER_PX;
    const ry = clearY + SPOTLIGHT_FEATHER_PX;
    const inner = Math.round(Math.min(clearX / rx, clearY / ry) * 100);
    const spot = el('div', 'mado-spot');
    spot.style.background =
      `radial-gradient(ellipse ${rx.toFixed(1)}px ${ry.toFixed(1)}px at ${cx.toFixed(1)}px ${cy.toFixed(1)}px, ` +
      `rgba(0,0,0,0) ${inner}%, ${this.style.spotColor} 100%)`;
    if (settleMs > 0) spot.style.transitionDelay = `${dur(settleMs)}ms`;
    this.cam.appendChild(spot);
    showIn(spot, 'mado-spot--in', instant);
  }

  /** Take the bubble away (Watch began on a step whose words are now spoken). */
  removeTooltip(): void {
    this.root.querySelector('.mado-tooltip')?.remove();
    this.clearDock();
    this.guide?.remove();
    this.guide = null;
    this.guideWanted = false;
  }

  /**
   * ArrowRight on the instruction card: its control pressed, as a key on the control presses it
   * (one `hotspot_click`; a type target types first). False: no instruction card is up.
   */
  pressTarget(step: Step): boolean {
    if (!this.cam.querySelector('.mado-callout--do')) return false;
    const a = step.annotation?.anchor;
    const k = Math.max(
      0,
      step.hotspots.findIndex((h) => h.id === a),
    );
    const b = this.cam.querySelectorAll<HTMLButtonElement>('.mado-hotspot')[k];
    b?.click();
    return !!b;
  }

  /** The step `typingOnClick` types into (`render` keeps its frame while it does). */
  private typingStepId: string | null = null;

  /** A click on a type target is typing out: a redraw now would cancel it and its advance. */
  get typingNow(): boolean {
    return this.typingOnClick;
  }

  /** The narration line under an instruction, unless it only repeats `action`. */
  private sayFor(step: Step, action: string): string {
    const say = (this.cb.say?.(step) ?? '').trim();
    return say && norm(say) !== norm(action) ? say : '';
  }

  /**
   * The instruction's words: the step count (bar aside only), the action line, the narration.
   * `card`: the callouts' card, the control the line names in bold as a callout's phrase is.
   */
  private words(kicker: string, action: string, say: string, card = false): HTMLElement {
    const body = el('span', 'mado-tooltip-b');
    const line = (cls: string, text: string, span: [number, number] | null = null): void => {
      if (!text) return;
      const n = el('span', cls);
      boldIn(n, text, span);
      body.appendChild(n);
    };
    line('mado-tooltip-n', kicker);
    line(
      card ? 'mado-tooltip-t mado-callout-t' : 'mado-tooltip-t',
      action,
      card ? controlSpan(action) : null,
    );
    line('mado-tooltip-say', say);
    return body;
  }

  /** A card's Back, when it goes somewhere. */
  private backs(f: { back: boolean } | null): HTMLElement[] {
    return f?.back
      ? [backButton((via) => this.cb.onBack?.(via === 'button'), this.style.s?.back)]
      : [];
  }

  /**
   * Where a ring's label may go (stage.ts `addLabel`), in viewport px: the story's window, else
   * the viewport above the chrome bar; and the boxes it must stay off (the tooltip, the guide card,
   * the targets: the control stays seen and pressable).
   */
  /** The band kept clear for the chrome bar at the viewport's foot (none when it is docked). */
  private get clearPx(): number {
    return this.cb.docked?.() ? 8 : CHROME_CLEAR_PX;
  }

  /** Empty the place outside the screen (`dockCard`) along with the screen's own cards. */
  private clearDock(): void {
    this.dockBox?.replaceChildren();
  }

  labelRoom(): { area: Rect; avoid: Rect[] } {
    const host = this.root.getBoundingClientRect();
    const w = this.root.clientWidth || host.width;
    const h = this.root.clientHeight || host.height;
    const area = this.bounds ?? { x: 0, y: 0, w, h: Math.max(0, h - this.clearPx) };
    const avoid: Rect[] = [];
    this.root.querySelectorAll('.mado-tooltip,.mado-hotspot').forEach((n) => {
      const r = n.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        avoid.push({
          x: r.left - host.left - 6,
          y: r.top - host.top - 6,
          w: r.width + 12,
          h: r.height + 12,
        });
      }
    });
    return { area, avoid };
  }

  /**
   * A step with nothing to tap (an overview, the closing screen): a card with
   * the step's words and a Next button. Without it a clicking viewer is shown
   * a screen and no way on. At the foot of the picture, or at its top when
   * what the step points at (its marks, shots, anchor) covers less there.
   */
  /**
   * Put the step's card in the place outside the screen (`dockCard`), when there is one: no
   * notch, nothing on the screen covered. False: it goes on the screen as before.
   */
  private toDock(card: HTMLElement, settleMs: number, instant: boolean): boolean {
    const box = this.cb.dockCard?.() ?? null;
    if (!box) return false;
    this.dockBox = box;
    box.replaceChildren(card);
    card.classList.add('mado-tooltip--dock');
    if (settleMs > 0) card.style.transitionDelay = `${dur(settleMs)}ms`;
    showIn(card, 'mado-tooltip--in', instant);
    return true;
  }

  private renderGuide(
    step: Step,
    layout: Layout,
    asset: { w: number; h: number },
    settleMs = 0,
    instant = false,
  ): void {
    this.guideWanted = true;
    const line = step.annotation?.text.trim() ?? '';
    // A phone story already says the line as the headline above the window: the card under it
    // does not say it again (2026-11-03 review: the last step read the same sentence twice).
    const action = this.headline ? '' : line;
    const say = this.sayFor(step, line);
    const watching = !!this.cb.watching?.();
    // Watch has nothing to show on it (no words left, and it moves on by itself): no card. An
    // empty card was a bare tile drawn over the demo's last screen (2026-11-03 review).
    if (watching && !action && !say) return;
    // The callout's card (callout.ts): tile, words, and Next in the same foot.
    const card = el('div', 'mado-tooltip mado-tooltip--say mado-guide');
    const body = this.words('', action, say);
    if (!watching) {
      const f = this.cb.foot?.(step) ?? null;
      body.appendChild(
        cardFoot(
          f?.count ?? '',
          ...this.backs(f),
          cardButton(
            'mado-end-again mado-callout-next mado-guide-next',
            this.style.s?.next ?? 'Next',
            (via) => this.cb.onNext?.(via === 'button'),
          ),
        ),
      );
    }
    card.append(tile(), body);
    this.guide = card;
    if (this.toDock(card, settleMs, instant)) return;
    this.root.appendChild(card);

    const host = this.root.getBoundingClientRect();
    const area: Rect = this.bounds ?? { x: 0, y: 0, w: host.width, h: host.height };
    const box = card.getBoundingClientRect();
    const w = Math.min(box.width, area.w);
    const h = Math.min(box.height, area.h);
    const left = area.x + (area.w - w) / 2;
    // The foot: above the chrome bar on the whole viewport, inside the window's edge in a story.
    const bottomY = this.bounds ? area.y + area.h - h - 12 : area.y + area.h - this.clearPx - h - 8;
    const topY = area.y + 12;
    const shown: Rect[] = [];
    for (const c of step.cues ?? []) shown.push(projectRect(c.rect, layout, asset));
    for (const s of step.shots ?? []) shown.push(projectRect(s.rect, layout, asset));
    const anchor = step.annotation?.anchor;
    if (anchor && typeof anchor !== 'string') shown.push(projectRect(anchor, layout, asset));
    const at = (y: number): Rect => ({ x: left, y, w, h });
    const foot = Math.max(topY, bottomY);
    card.style.left = `${left}px`;
    card.style.top = `${overlap(at(topY), shown) < overlap(at(foot), shown) ? topY : foot}px`;
    if (settleMs > 0) card.style.transitionDelay = `${dur(settleMs)}ms`;
    showIn(card, 'mado-tooltip--in', instant);
  }

  /**
   * Watch started or stopped with a guide card up: draw it again, without its buttons in Watch
   * (it advances by itself) and with Next in click-through.
   */
  redrawGuide(step: Step, layout: Layout, asset: { w: number; h: number }): void {
    if (!this.guide && !this.guideWanted) return;
    this.guide?.remove();
    this.guide = null;
    this.renderGuide(step, layout, asset, 0, true);
  }

  /** Put the bubble back (Watch stopped on a step whose words it had been saying). */
  restoreTooltip(step: Step, layout: Layout, asset: { w: number; h: number }): void {
    if (this.root.querySelector('.mado-tooltip') || this.dockBox?.firstChild) return;
    if (!step.annotation?.text.trim() || this.callouts || this.headline) return;
    if (this.cb.spokenAloud?.(step)) return;
    this.renderTooltip(step, layout, asset);
  }

  private renderTooltip(
    step: Step,
    layout: Layout,
    asset: { w: number; h: number },
    settleMs = 0,
    instant = false,
    underBar = false,
  ): void {
    const a = step.annotation!;
    // Belt and braces: the caller already checks `.text.trim()`, but this is
    // the one place that would draw an empty box if some other path ever
    // called it without that guard.
    if (!a.text.trim()) return;
    const anchor = this.anchorRect(step, layout, asset);

    // A tooltip points at something. Once a scroll has carried that something
    // off the frame, the tooltip has nothing to say and would otherwise clamp
    // to the top edge and sit on the sticky header.
    const host0 = this.root.getBoundingClientRect();
    if (anchor && (anchor.y + anchor.h < 0 || anchor.y > host0.height)) return;

    // A four-pane tile (the logo's shape) beside the words: the action line
    // ("Click Jobs to …") and, when the words are not said aloud, the step's
    // narration under it: the instruction a clicking viewer reads.
    // While the bar has stepped aside for the target, the step count rides here.
    // Click-through: the callouts' card, the last of the step's cards, with their foot: the count,
    // Back, and where the way on is ("Click Jobs" and an arrow at it). The hint is not a button:
    // the step goes on by the control itself (or ArrowRight), as in the product.
    const f = this.walk ? null : (this.cb.foot?.(step) ?? null);
    const tip = el(
      'div',
      f ? 'mado-tooltip mado-tooltip--say mado-callout mado-callout--do' : 'mado-tooltip',
    );
    const say = this.sayFor(step, a.text);
    if (say) tip.classList.add('mado-tooltip--say');
    const body = this.words(underBar ? (this.cb.progress?.() ?? '') : '', a.text, say, !!f);
    if (f) {
      const span = controlSpan(a.text);
      const target =
        step.hotspots.find((h) => h.id === a.anchor) ??
        (step.hotspots.length === 1 ? step.hotspots[0] : null);
      const go = el('span', 'mado-callout-go');
      go.textContent = span
        ? a.text.slice(0, span[1]).trim()
        : target?.label
          ? (this.style.s?.click(target.label) ?? `Click ${target.label}`)
          : '';
      body.appendChild(cardFoot(f.count, ...this.backs(f), ...(go.textContent ? [go] : [])));
    }
    tip.append(tile(), body);
    if (this.toDock(tip, settleMs, instant)) return;
    this.cam.appendChild(tip);

    // Measure after insertion, then place. Position is set with left/top on an
    // absolutely-positioned element — no layout is triggered for anything else.
    let box = tip.getBoundingClientRect();
    const host = this.root.getBoundingClientRect();
    // Its words are never cut (2026-10-16: a muted Watch card ended "When the sto…"). When the
    // whole line is taller than the room either side of the target, the card takes the stage's
    // width and a smaller type before it would cover the ring.
    if (anchor) {
      const room = Math.max(anchor.y, host.height - this.clearPx - (anchor.y + anchor.h));
      if (box.height + TIP_GAP_PX > room) {
        tip.classList.add('mado-tooltip--snug');
        box = tip.getBoundingClientRect();
      }
    }
    // Room for the notch that points at the target.
    const gap = TIP_GAP_PX;

    let top: number;
    let left: number;
    let isAbove = false;

    if (!anchor) {
      left = (host.width - box.width) / 2;
      top = host.height - box.height - 72;
    } else {
      const below = anchor.y + anchor.h + gap;
      const above = anchor.y - box.height - gap;
      const fitsBelow = below + box.height <= host.height - this.clearPx;
      const fitsAbove = above >= 8;
      // Auto: below, unless that lands on the chrome; then above. When
      // neither fits — a zoomed target nearly as tall as a short box, a
      // landscape tablet on a portrait phone — the side with more room, and
      // the tooltip closes the gap to the target rather than covering the
      // controls.
      const roomAbove = anchor.y;
      const roomBelow = host.height - this.clearPx - (anchor.y + anchor.h);
      const wantsAbove =
        a.place === 'top' ||
        (a.place === 'auto' && !fitsBelow && (fitsAbove || roomAbove > roomBelow));
      top = wantsAbove ? above : below;
      isAbove = wantsAbove;
      left = anchor.x + anchor.w / 2 - box.width / 2;
    }

    const placedLeft = clamp(left, 8, Math.max(8, host.width - box.width - 8));
    tip.style.left = `${placedLeft}px`;
    if (anchor) {
      // The notch points at the target's centre, kept inside the rounded corners.
      const aimX = anchor.x + anchor.w / 2 - placedLeft;
      tip.style.setProperty('--mado-notch', `${clamp(aimX, 22, Math.max(22, box.width - 22))}px`);
      tip.classList.add(isAbove ? 'mado-tooltip--above' : 'mado-tooltip--below');
    }
    // Never over the chrome bar: the progress and the Watch button stay legible.
    tip.style.top = `${clamp(top, 8, Math.max(8, host.height - this.clearPx - box.height))}px`;
    // Read after the camera has arrived, not while the picture is still moving.
    if (settleMs > 0) tip.style.transitionDelay = `${dur(settleMs)}ms`;

    showIn(tip, 'mado-tooltip--in', instant);
  }

  /**
   * Simulated typing. The pipeline records the text; we replay it at a human
   * cadence so an input step reads as someone using the product rather than a
   * screenshot with words already in it.
   */
  private renderTyping(
    step: Step,
    layout: Layout,
    asset: { w: number; h: number },
    done?: () => void,
    afterMs = 0,
  ): void {
    const input = step.input!;
    const target = step.hotspots.find((h) => h.id === input.target);
    if (!target) return;

    const field = projectRect(target.geom, layout, asset);
    // In a story the box is the part of the field inside the window; a side
    // the window cuts loses its rounding, so the field reads as continuing
    // past the frame rather than ending at it.
    const r = clipRect(field, this.bounds);
    const box = el('div', 'mado-typed');
    if (r !== field) {
      const cutL = r.x > field.x + 0.5;
      const cutR = r.x + r.w < field.x + field.w - 0.5;
      const cutT = r.y > field.y + 0.5;
      const cutB = r.y + r.h < field.y + field.h - 0.5;
      const k = (a: boolean, b: boolean) => (a || b ? '0' : '6px');
      box.style.borderRadius = `${k(cutT, cutL)} ${k(cutT, cutR)} ${k(cutB, cutR)} ${k(cutB, cutL)}`;
    }
    box.style.left = `${r.x}px`;
    box.style.top = `${r.y}px`;
    box.style.width = `${r.w}px`;
    box.style.height = `${r.h}px`;
    box.style.fontSize = `${Math.min(16, r.h * 0.44)}px`;
    // A field wider than the viewport (a web form under the legibility zoom
    // on a phone) runs off the glass on one side or both. The box keeps the
    // field's own edges — so its corners are never seen — but its text area
    // is only the part of the field on screen: the padding grows by whatever
    // is off-screen. Text that outgrows that area scrolls to keep the caret
    // in view (`.mado-typed` in styles.ts), as a real input does.
    const left = this.bounds ? this.bounds.x : 0;
    const right = this.bounds ? this.bounds.x + this.bounds.w : this.root.clientWidth || r.x + r.w;
    const padL = TYPED_PAD_PX + Math.max(0, left - r.x);
    const padR = TYPED_PAD_PX + Math.max(0, r.x + r.w - right);
    box.style.padding = `0 ${padR}px 0 ${padL}px`;

    const text = document.createElement('span');
    const caret = el('i', 'mado-caret');
    box.append(text, caret);
    this.cam.appendChild(box);

    const ms = (input.text.length / input.cps) * 1000;
    // `typing` is non-null exactly while text is still arriving (or its
    // closing beat is running): that is what `cancelTyping` may undo. Once
    // the text is complete it stays on screen whatever happens next.
    let finished = false;
    const start = (): void => {
      const handle = tween(
        dur(ms),
        (t) => {
          text.textContent = input.text.slice(0, Math.round(t * input.text.length));
        },
        () => {
          finished = true;
          if (!done) {
            this.typing = null;
            return;
          }
          // A beat on the finished text before moving on, so it can be read.
          const t = window.setTimeout(() => {
            this.typing = null;
            done();
          }, dur(TYPED_PAUSE_MS));
          this.typing = { cancel: () => clearTimeout(t) };
        },
        linear,
      );
      // A zero-length tween (reduced motion) has already finished synchronously.
      if (!finished) this.typing = handle;
    };
    if (afterMs > 0) {
      // Wait for the camera; cancellable like the tween it becomes.
      const t = window.setTimeout(start, afterMs);
      this.typing = { cancel: () => clearTimeout(t) };
    } else {
      start();
    }
  }

  /** Reposition on resize without rebuilding — cheaper and avoids flicker. */
  reposition(step: Step, layout: Layout, asset: { w: number; h: number }): void {
    // Mid-typing after a click, a rebuild would cancel the typing and with it
    // the advance it ends in: keep the frame, the step is about to change.
    if (this.typingOnClick) return;
    // A resize mid-entrance: the picture is still sliding in; so are the targets.
    this.render(step, layout, asset, { settleMs: 0, keepEntrance: true });
  }
}

/** Fade `n` in by adding `cls`, or with `instant` show it in at once (no transition). */
function showIn(n: HTMLElement, cls: string, instant: boolean): void {
  if (instant) n.style.transition = 'none';
  reflow(n);
  n.classList.add(cls);
  if (instant) {
    reflow(n);
    n.style.transition = '';
  }
}

/** The typed box's inset from the field's edge; must match `.mado-typed` in styles.ts. */
const TYPED_PAD_PX = 8;
/**
 * The chrome bar (44px of controls plus its fade) at the bottom of the
 * viewport: a tooltip is kept above it, whatever else has to give. Also the
 * band a target has to reach into for the bar to yield (`overlapsChrome`).
 * The tooltip keeps clear of it even then: a target in the band puts its
 * tooltip above itself either way, and a bar that comes back on the next
 * step should not find the tooltip's place changed.
 */
export const CHROME_CLEAR_PX = 56;
/**
 * The band the chrome bar covers, for stepping aside: the bar as laid out, never under
 * `CHROME_CLEAR_PX` and never over 96 px (a bar is about 64 px, 80 with a wrapped badge; a
 * measurement past that is a layout gone wrong, and the bar must not vanish on every step).
 */
export function chromeBand(measured: number): number {
  return Math.min(96, Math.max(CHROME_CLEAR_PX, measured || 0));
}
/** Room left for the notch between a target and its tooltip. */
const TIP_GAP_PX = 16;
/** Length of the post-miss nudge; must match `mado-nudge` in styles.ts. */
const NUDGE_MS = 800;

/**
 * What the viewer is asked to do, drawn inside the target so a swipe, a
 * scroll and a text field do not all read as "click here". Transform and
 * opacity only (CLAUDE.md rule 12); stilled under reduced motion.
 */
function gestureHint(h: Hotspot): HTMLElement | null {
  if (h.gesture === 'swipe') {
    const hint = el('span', `mado-hint mado-hint--swipe mado-hint--${h.dir ?? 'left'}`);
    hint.appendChild(el('span', 'mado-hint-dot'));
    return hint;
  }
  if (h.gesture === 'scroll') {
    const hint = el('span', 'mado-hint mado-hint--scroll');
    hint.append(el('span', 'mado-hint-chev'), el('span', 'mado-hint-chev'));
    return hint;
  }
  if (h.gesture === 'type') {
    const hint = el('span', 'mado-hint mado-hint--type');
    hint.appendChild(el('i', 'mado-caret'));
    return hint;
  }
  return null;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Text compared loosely: case, spacing and punctuation ignored. */
const norm = (t: string) =>
  t
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
